import { Container, Divider } from "@material-ui/core";
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  CurrencyDisplay,
  CustomTable,
  FormCard,
  ModalError,
  Page,
  SectionLabel,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { Fragment, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, stringToArray, wordCapitalize } from "utils";
import AdminDetailPotentialClaimLog from "pages/Admin/PotentialClaim/DetailPotentialClaimLog";

const DetailPotentialClaim = () => {
  const userRole = localStorage?.getItem("role");
  if (userRole === "admin") {
    return <AdminDetailPotentialClaimLog />;
  }

  const classes = globalStyles();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const { id } = useParams();
  const pageTitle = "Detail Potential Claim Log";

  const [loadingPage, setLoadingPage] = useState(true);
  const [pageDetail, setPageDetail] = useState({
    status: "",
  });
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [loadingChildrenTable, setLoadingChildrenTable] = useState(false);
  const [queryParamsChildren, setQueryParamsChildren] = useState({
    page: 1,
    size: 10,
    song_id: 0,
  });
  const [dataTableChildren, setDataTableChildren] = useState([]);
  const [tablePageCountChildren, setTablePageCountChildren] = useState(1);
  const { status } = pageDetail || {};
  const errorLog = status === "error";

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeQueryParamsChildren = (value, key) => {
    setQueryParamsChildren(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getDetail = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/potential-claim/claim-logs/${id}`,
        { headers }
      );
      setPageDetail(res?.data?.data);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };
  const getDataTable = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/potential-claim/claim-logs/${id}/songs`,
        {
          headers,
          params: queryParams,
        }
      );
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [];
    if (isFirstLoad.current) promises.push(getDetail());
    promises.push(getDataTable());
    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };
  const getDataChildrenTable = async () => {
    const { page, size } = queryParamsChildren;
    setLoadingChildrenTable(true);
    try {
      const res = await axios.get(
        `${hardBaseUrl}/potential-claim/claim-logs/${id}/songs/${queryParamsChildren?.song_id}`,
        {
          headers,
          params: { page, size },
        }
      );
      const { data, meta } = res?.data;
      setDataTableChildren(data || []);
      const pageCount = getTotalPage(meta?.total, queryParamsChildren?.size);
      setTablePageCountChildren(pageCount || 1);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingChildrenTable(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [queryParams]);
  useEffect(() => {
    if (queryParamsChildren?.song_id) {
      getDataChildrenTable();
    }
  }, [queryParamsChildren]);

  return (
    <Page className={classes.root} title={pageTitle}>
      <Container maxWidth="false">
        <HeaderTitle
          title={pageTitle}
          breadcrumbData={breadcrumbData({ pageTitle })}
          backButton
        />
        <Divider className={classes.divider} />
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <SectionLabel
              title="Detail of Potential Claim Log"
              subTitle="View detail data for potential claim log"
            />
          </Grid>
          <Grid item>
            <StatusChip
              label={wordCapitalize(status) || "-"}
              type={
                status === "finished"
                  ? "success"
                  : status === "time-out" || status === "error"
                  ? "danger"
                  : status === "started"
                  ? "warning"
                  : ""
              }
            />
          </Grid>
        </Grid>
        {loadingPage ? (
          <Box my={2}>
            <Skeleton variant="rounded" height="170px" animation="wave" />
          </Box>
        ) : (
          <FormCard>
            <Grid container rowGap={1}>
              {mockfieldForm({ errorLog }).map((field, index) => (
                <Fragment key={index}>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography className={classes?.fieldTitle}>
                      {field?.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} md={10}>
                    <Typography className={classes?.fieldValue}>
                      {field?.type === "number" ? (
                        <CurrencyDisplay
                          value={pageDetail?.[field?.value]}
                          prefix={field?.prefix}
                          textAlign="left"
                        />
                      ) : (
                        pageDetail?.[field?.value] || "-"
                      )}
                    </Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </FormCard>
        )}
        {!errorLog && (
          <CustomTable
            isLoading={loadingPage}
            columnTable={columnTable}
            data={dataTable || []}
            page={queryParams?.page}
            pageSize={queryParams?.size}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            isExpandable
            expandedRowKey="id"
            handleExpandableRow={item =>
              handleChangeQueryParamsChildren(item?.song_id, "song_id")
            }
            renderExpandedRow={() => (
              <CustomTable
                isLoading={loadingChildrenTable}
                columnTable={columnTableChildren}
                data={dataTableChildren || []}
                page={queryParamsChildren?.page}
                pageSize={queryParamsChildren?.size}
                pageCount={tablePageCountChildren}
                handleChangePage={(_, page) =>
                  handleChangeQueryParamsChildren(page, "page")
                }
                handleChangePageSize={e =>
                  handleChangeQueryParamsChildren(e?.target?.value, "size")
                }
                maxHeight={300}
                tableSize="small"
              />
            )}
            my={2}
          />
        )}
      </Container>
    </Page>
  );
};
const mockfieldForm = ({ errorLog }) =>
  errorLog
    ? [{ label: "Error Message", value: "error_details" }]
    : [
        { label: "Document Number", value: "document_number" },
        { label: "DSP Name", value: "dsp_name" },
        {
          label: "Total Traffic",
          value: "total_streams_claimed",
          type: "number",
        },
        {
          label: "Total Revenue",
          value: "revenue_claimed",
          type: "number",
          prefix: "Rp",
        },
      ];
export const breadcrumbData = ({ pageTitle }) => [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Potential Claim",
    link: "/admin/publisher/potential-claim-log",
  },
  {
    label: "Potential Claim Log",
    link: "/admin/publisher/potential-claim-log",
  },
  {
    label: pageTitle,
    active: true,
  },
];
const columnTable = [
  {
    name: "song_title",
    title: "Song Title",
    renderText: item => <Typography fontWeight={700}>{item || "-"}</Typography>,
  },
  {
    name: "composer",
    title: "Composer",
    renderText: item => {
      const list = stringToArray(item, "|");
      return <ArrayChip list={list} />;
    },
  },
  {
    name: "isrc",
    title: "ISRC Code",
    renderText: item => {
      const list = stringToArray(item, "|");
      return <ArrayChip list={list} />;
    },
  },
  {
    name: "traffic",
    title: "Traffic",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
  {
    name: "revenue",
    title: "Revenue",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
  },
  {
    name: "period_date",
    title: "Period Date",
  },
];
const columnTableChildren = [
  {
    name: "song_title",
    title: "Song Title",
    renderText: item => <Typography fontWeight={700}>{item || "-"}</Typography>,
  },
  {
    name: "composer",
    title: "Composer",
    renderText: item => {
      const list = stringToArray(item, "|");
      return <ArrayChip list={list} />;
    },
  },
  {
    name: "isrc",
    title: "ISRC Code",
    renderText: item => {
      const list = stringToArray(item, "|");
      return <ArrayChip list={list} />;
    },
  },
  {
    name: "traffic",
    title: "Traffic",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
  {
    name: "revenue",
    title: "Revenue",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
  },
  {
    name: "period_date",
    title: "Period Date",
  },
];

export default DetailPotentialClaim;
